import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MoreHorizontal, Edit, Trash2, Calendar, DollarSign, Archive, RotateCcw, Users } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useCompensationAssignments } from '@/hooks/compensation';
import { useWorkplaceCurrency } from '@/contexts/WorkplaceCurrencyContext';
import type { CompensationRule } from '@/services/compensation/types';
import { format } from 'date-fns';
import { type TaxBracketConfig } from '../../../shared/compensation/tax';

// Assignment Count component
function AssignmentCount({ ruleId }: { ruleId: string }) {
  const { useRuleAssignments } = useCompensationAssignments();
  const { data: assignments } = useRuleAssignments(ruleId);
  
  const activeAssignments = assignments?.filter(a => a.is_active) || [];
  
  if (activeAssignments.length === 0) {
    return (
      <div className="flex items-center gap-1 text-xs text-muted-foreground">
        <Users className="h-3 w-3" />
        <span>No assignments</span>
      </div>
    );
  }
  
  return (
    <div className="flex items-center gap-1 text-xs text-primary">
      <Users className="h-3 w-3" />
      <span>{activeAssignments.length} employee{activeAssignments.length !== 1 ? 's' : ''}</span>
    </div>
  );
}

interface CompensationRuleCardProps {
  rule: CompensationRule;
  onEdit: (rule: CompensationRule) => void;
  onDeactivate: (ruleId: string) => void;
  onDelete: (ruleId: string) => void;
  onReactivate: (ruleId: string) => void;
  onViewEmployees: (rule: CompensationRule) => void;
}

const getRuleTypeLabel = (ruleType: string) => {
  const labels: Record<string, string> = {
    hourly_rate: 'Hourly Rate',
    salary: 'Salary',
    overtime: 'Overtime',
    allowance: 'Allowance',
    deduction: 'Deduction',
    bonus: 'Bonus'
  };
  return labels[ruleType] || ruleType;
};

const getCalculationMethodLabel = (method: string) => {
  const labels: Record<string, string> = {
    fixed_amount: 'Fixed Amount',
    percentage: 'Percentage',
    tiered_rate: 'Tiered Rate',
    multiplier: 'Multiplier'
  };
  return labels[method] || method;
};

const getRuleTypeVariant = (ruleType: string): "default" | "secondary" | "destructive" | "outline" => {
  const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
    hourly_rate: 'default',
    salary: 'default',
    overtime: 'secondary',
    allowance: 'outline',
    deduction: 'destructive',
    bonus: 'outline'
  };
  return variants[ruleType] || 'default';
};

export function CompensationRuleCard({ rule, onEdit, onDeactivate, onDelete, onReactivate, onViewEmployees }: CompensationRuleCardProps) {
  const { currencyCode } = useWorkplaceCurrency();
  
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
    }).format(amount);
  };

  const getDisplayAmount = () => {
    switch (rule.calculation_method) {
      case 'fixed_amount':
        return formatCurrency(rule.base_rate || 0);
      case 'percentage':
        return `${rule.percentage_rate || 0}%`;
      case 'multiplier':
        return `${rule.multiplier_rate || 1}x`;
      case 'tiered_rate':
        return 'Tiered';
      default:
        return formatCurrency(rule.base_rate || 0);
    }
  };

  return (
    <Card className="transition-shadow hover:shadow-md">
      <CardHeader className="pb-2">
        <div className="relative">
          <div className="space-y-1 pr-8">
            <div className="flex items-center gap-2 flex-wrap">
              <CardTitle className="text-base">{rule.rule_name}</CardTitle>
              <Badge variant={getRuleTypeVariant(rule.rule_type)} className="text-xs">
                {getRuleTypeLabel(rule.rule_type)}
              </Badge>
              {rule.rule_type === 'deduction' && (rule as any).deduction_category === 'tax' && (
                <Badge variant="outline" className="bg-yellow-500/10 text-yellow-700 dark:text-yellow-500 border-yellow-500/20 text-xs">
                  Tax
                </Badge>
              )}
              {rule.rule_type === 'salary' && (rule as any).salary_mode === 'monthly_fixed' && (
                <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20 text-xs">
                  Monthly Fixed
                </Badge>
              )}
              {rule.rule_type === 'deduction' && (rule as any).deduction_category === 'other' && (rule as any).deduction_type && (
                <Badge variant="outline" className="bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20 text-xs">
                  {(rule as any).deduction_type === 'per_pay_period' && 'Per Period'}
                  {(rule as any).deduction_type === 'per_worked_day' && 'Per Worked Day'}
                  {(rule as any).deduction_type === 'percentage_of_gross' && '% of Gross'}
                </Badge>
              )}
              {!rule.is_active && (
                <Badge variant="outline" className="text-muted-foreground text-xs">
                  Inactive
                </Badge>
              )}
            </div>
            {rule.description && (
              <CardDescription className="text-xs">{rule.description}</CardDescription>
            )}
            <AssignmentCount ruleId={rule.id} />
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="absolute top-0 right-0 h-6 w-6 p-0">
                <MoreHorizontal className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onEdit(rule)}>
                <Edit className="h-4 w-4 mr-2" />
                Edit Rule
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onViewEmployees(rule)}>
                <DollarSign className="h-4 w-4 mr-2" />
                View Assignments
              </DropdownMenuItem>
              
              {rule.is_active ? (
                <>
                  <DropdownMenuItem onClick={() => onDeactivate(rule.id)}>
                    <Archive className="h-4 w-4 mr-2" />
                    Deactivate Rule
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => onDelete(rule.id)}
                    className="text-destructive"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Rule
                  </DropdownMenuItem>
                </>
              ) : (
                <>
                  <DropdownMenuItem onClick={() => onReactivate(rule.id)}>
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Reactivate Rule
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => onDelete(rule.id)}
                    className="text-destructive"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Rule
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>

      <CardContent className="pt-0 space-y-2">
        <div className="grid grid-cols-3 gap-3 text-xs">
          <div className="min-w-0">
            <span className="text-muted-foreground">Method:</span>
            <br />
            <span className="font-medium truncate block">{getCalculationMethodLabel(rule.calculation_method)}</span>
          </div>
          
          <div className="min-w-0">
            <span className="text-muted-foreground">Amount:</span>
            <br />
            <span className="font-semibold truncate block" title={getDisplayAmount()}>{getDisplayAmount()}</span>
          </div>
          
          <div className="min-w-0">
            <span className="text-muted-foreground">Effective:</span>
            <br />
            <div className="flex items-center gap-1 flex-nowrap">
              <Calendar className="h-3 w-3 flex-shrink-0" />
              <span className="truncate">{format(new Date(rule.effective_from), 'MMM d')}</span>
              {rule.effective_to && (
                <>
                  <span className="text-muted-foreground flex-shrink-0">-</span>
                  <span className="truncate">{format(new Date(rule.effective_to), 'MMM d')}</span>
                </>
              )}
            </div>
          </div>
        </div>

        {rule.applies_to_holidays && (
          <div className="flex items-center gap-2 pt-1">
            <span className="text-xs text-muted-foreground">Applies to:</span>
            <div className="flex gap-1">
              {rule.applies_to_holidays && (
                <Badge variant="outline" className="text-xs h-5">
                  {rule.rule_type === 'allowance' 
                    ? 'Holidays' 
                    : `Holidays ${(rule as any).holiday_multiplier ? `(${(rule as any).holiday_multiplier}x)` : '(2x)'}`}
                </Badge>
              )}
            </div>
          </div>
        )}

        {/* Salary Schedule Summary */}
        {rule.rule_type === 'salary' && ((rule as any).workweek_days || (rule as any).scheduled_days || (rule as any).ot_tiers) && (
          <div className="mt-3 pt-3 border-t space-y-2">
            <p className="text-xs font-medium text-muted-foreground">Salary Schedule:</p>
            <div className="space-y-1">
              {(rule as any).workweek_days && (
                <div className="text-xs flex justify-between items-center">
                  <span className="text-muted-foreground">Workweek:</span>
                  <span className="font-medium">{(rule as any).workweek_days} days</span>
                </div>
              )}
              {(rule as any).scheduled_days && (rule as any).scheduled_days.length > 0 && (
                <div className="text-xs flex justify-between items-center">
                  <span className="text-muted-foreground">Scheduled Days:</span>
                  <span className="font-medium">
                    {(() => {
                      const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                      const scheduledDays = (rule as any).scheduled_days as number[];
                      return scheduledDays.map(d => days[d]).join(', ');
                    })()}
                  </span>
                </div>
              )}
              {(rule as any).ot_tiers && (rule as any).ot_tiers.length > 0 && (
                <div className="text-xs">
                  <span className="text-muted-foreground">Non-Scheduled Day OT:</span>
                  <div className="mt-1 space-y-0.5 ml-2">
                    {((rule as any).ot_tiers as any[]).map((tier, i) => (
                      <div key={i} className="flex justify-between text-xs">
                        <span className="text-muted-foreground">
                          {tier.from_hours}h - {tier.to_hours ? `${tier.to_hours}h` : '∞'}
                        </span>
                        <span className="font-medium">{tier.multiplier}x</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Tax Bracket Summary */}
        {rule.rule_type === 'deduction' && (rule as any).deduction_category === 'tax' && (rule as any).tax_brackets && (
          <div className="mt-3 pt-3 border-t space-y-2">
            <p className="text-xs font-medium text-muted-foreground">Progressive Tax Brackets:</p>
            {(() => {
              const config = (rule as any).tax_brackets as TaxBracketConfig;
              if (!config || !config.tiers) return null;
              
              const sortedTiers = [...config.tiers].sort((a, b) => a.lower_bound - b.lower_bound);
              const fiscalMonth = config.fiscal_year_start_month ?? 1;
              const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
              
              return (
                <div className="space-y-1">
                  {sortedTiers.map((tier, i) => (
                    <div key={i} className="text-xs flex justify-between items-center py-1">
                      <span className="text-muted-foreground">
                        {formatCurrency(tier.lower_bound)} - {tier.upper_bound ? formatCurrency(tier.upper_bound) : '∞'}
                      </span>
                      <span className="font-medium">{tier.rate_percent}%</span>
                    </div>
                  ))}
                  <div className="text-xs text-muted-foreground italic mt-2">
                    Uses YTD annual calculation
                    {fiscalMonth !== 1 && ` (fiscal year starts ${monthNames[fiscalMonth - 1]})`}
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* Other Deduction Summary */}
        {rule.rule_type === 'deduction' && (rule as any).deduction_category === 'other' && (rule as any).deduction_type && (
          <div className="mt-3 pt-3 border-t space-y-1">
            <p className="text-xs font-medium text-muted-foreground">Deduction Details:</p>
            <div className="text-xs flex justify-between items-center">
              <span className="text-muted-foreground">Type:</span>
              <span className="font-medium">
                {(rule as any).deduction_type === 'per_pay_period' && 'Fixed Per Pay Period'}
                {(rule as any).deduction_type === 'per_worked_day' && 'Per Worked Day'}
                {(rule as any).deduction_type === 'percentage_of_gross' && `${rule.percentage_rate || 0}% of Gross`}
              </span>
            </div>
            {(rule as any).deduction_type === 'per_worked_day' && (rule as any).min_hours_per_day > 0 && (
              <div className="text-xs flex justify-between items-center">
                <span className="text-muted-foreground">Min Hours/Day:</span>
                <span className="font-medium">{(rule as any).min_hours_per_day}h</span>
              </div>
            )}
            {(rule as any).deduction_type === 'percentage_of_gross' && (rule as any).threshold_amount > 0 && (
              <div className="text-xs flex justify-between items-center">
                <span className="text-muted-foreground">Monthly Cap:</span>
                <span className="font-medium">{formatCurrency((rule as any).threshold_amount)}</span>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}